import { type HabitFilter, defaultHabitFilter } from '@goaltree/backend/convex/lib/habitView'
import {
  type TaskFilter,
  defaultTaskFilter,
  taskFilterFromLegacy,
} from '@goaltree/backend/convex/lib/taskView'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type GoalSortKey = 'manual' | 'progress' | 'remaining' | 'title'
export type GoalGroupKey = 'none' | 'category' | 'status'

export type GoalTypeKey = 'all' | 'starred' | 'recent'
export type InboxTab = 'tasks' | 'habits' | 'events'
export type EventsMode = 'upcoming' | 'past'

export interface GoalsFilter {
  categories: string[]
  sort: GoalSortKey
  group: GoalGroupKey
  type: GoalTypeKey
  hideCompleted: boolean
}

const defaultGoalsFilter: GoalsFilter = {
  categories: [],
  sort: 'manual',
  group: 'none',
  type: 'all',
  hideCompleted: false,
}

export type ViewMode = 'list' | 'kanban' | 'flow' | 'graph' | 'calendar'

export type CalendarView = 'month' | 'week' | 'day'

interface ViewState {
  view: ViewMode
  // Goals screen: whether the Vision board view is selected (replaced kanban).
  goalsVision: boolean
  // Goals list sections (Needs attention / On track / Done) the user folded.
  goalsCollapsed: string[]
  inboxView: 'list' | 'kanban' | 'calendar'
  inboxTab: InboxTab
  calendarView: CalendarView
  goalsFilter: GoalsFilter
  hideCompletedInbox: boolean
  compactHabitDots: boolean
  todayHabitDotsFull: boolean
  todayCompact: boolean
  showTaskNumbers: boolean
  // Everyday › Habits "Done today" group; null until the user folds or opens it.
  habitsDoneCollapsed: boolean | null
  setHabitsDoneCollapsed: (v: boolean) => void
  taskFilter: TaskFilter
  habitFilter: HabitFilter
  eventsMode: EventsMode
  setEventsMode: (m: EventsMode) => void
  tipsDismissed: boolean
  locationCardDismissed: boolean
  calendarCardDismissed: boolean
  pushNudgeDismissed: boolean
  // Newest friend activity seen on Today's Friends tab (ms), for its unread dot.
  friendsSeenAt: number
  // Hour (0–23, local) the user's day rolls over for habit/recurring completions
  // and streaks. Hydrated from server preferences; default 3am.
  dayStartHour: number
  setDayStartHour: (h: number) => void
  setView: (view: ViewMode) => void
  setGoalsVision: (v: boolean) => void
  toggleGoalsSection: (key: string) => void
  setInboxView: (v: 'list' | 'kanban' | 'calendar') => void
  setInboxTab: (v: InboxTab) => void
  setCalendarView: (v: CalendarView) => void
  setGoalsFilter: (f: Partial<GoalsFilter>) => void
  resetGoalsFilter: () => void
  setTaskFilter: (f: Partial<TaskFilter>) => void
  resetTaskFilter: () => void
  setHabitFilter: (f: Partial<HabitFilter>) => void
  resetHabitFilter: () => void
  toggleHideCompletedInbox: () => void
  toggleCompactHabitDots: () => void
  // Today's header switch: compact → detailed → detailed with full dot grids.
  cycleTodayDensity: () => void
  toggleTodayCompact: () => void
  toggleShowTaskNumbers: () => void
  dismissTips: () => void
  dismissLocationCard: () => void
  dismissCalendarCard: () => void
  dismissPushNudge: () => void
  setFriendsSeenAt: (at: number) => void
}

export const useViewStore = create<ViewState>()(
  persist(
    (set) => ({
      view: 'list',
      goalsVision: false,
      goalsCollapsed: [],
      inboxView: 'list',
      inboxTab: 'habits',
      calendarView: 'month',
      goalsFilter: defaultGoalsFilter,
      hideCompletedInbox: false,
      compactHabitDots: false,
      todayHabitDotsFull: false,
      todayCompact: false,
      showTaskNumbers: false,
      habitsDoneCollapsed: null,
      setHabitsDoneCollapsed: (habitsDoneCollapsed) => set({ habitsDoneCollapsed }),
      taskFilter: defaultTaskFilter,
      habitFilter: defaultHabitFilter,
      eventsMode: 'upcoming',
      setEventsMode: (eventsMode) => set({ eventsMode }),
      dayStartHour: 3,
      tipsDismissed: false,
      locationCardDismissed: false,
      calendarCardDismissed: false,
      pushNudgeDismissed: false,
      friendsSeenAt: 0,
      setDayStartHour: (dayStartHour) => set({ dayStartHour }),
      setView: (view) => set({ view }),
      setGoalsVision: (goalsVision) => set({ goalsVision }),
      toggleGoalsSection: (key) =>
        set((s) => ({
          goalsCollapsed: s.goalsCollapsed.includes(key)
            ? s.goalsCollapsed.filter((k) => k !== key)
            : [...s.goalsCollapsed, key],
        })),
      setInboxView: (inboxView) => set({ inboxView }),
      setInboxTab: (inboxTab) => set({ inboxTab }),
      setCalendarView: (calendarView) => set({ calendarView }),
      setGoalsFilter: (f) => set((s) => ({ goalsFilter: { ...s.goalsFilter, ...f } })),
      resetGoalsFilter: () => set({ goalsFilter: defaultGoalsFilter }),
      setTaskFilter: (f) => set((s) => ({ taskFilter: { ...s.taskFilter, ...f } })),
      resetTaskFilter: () => set({ taskFilter: defaultTaskFilter }),
      setHabitFilter: (f) => set((s) => ({ habitFilter: { ...s.habitFilter, ...f } })),
      resetHabitFilter: () => set({ habitFilter: defaultHabitFilter }),
      toggleHideCompletedInbox: () => set((s) => ({ hideCompletedInbox: !s.hideCompletedInbox })),
      toggleCompactHabitDots: () => set((s) => ({ compactHabitDots: !s.compactHabitDots })),
      cycleTodayDensity: () =>
        set((s) =>
          s.todayCompact
            ? { todayCompact: false, todayHabitDotsFull: false }
            : s.todayHabitDotsFull
              ? { todayCompact: true, todayHabitDotsFull: false }
              : { todayHabitDotsFull: true },
        ),
      toggleTodayCompact: () => set((s) => ({ todayCompact: !s.todayCompact })),
      toggleShowTaskNumbers: () => set((s) => ({ showTaskNumbers: !s.showTaskNumbers })),
      dismissTips: () => set({ tipsDismissed: true }),
      dismissLocationCard: () => set({ locationCardDismissed: true }),
      dismissCalendarCard: () => set({ calendarCardDismissed: true }),
      dismissPushNudge: () => set({ pushNudgeDismissed: true }),
      setFriendsSeenAt: (friendsSeenAt) => set({ friendsSeenAt }),
    }),
    {
      name: 'view-preferences',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      // v2: the Tasks tab has its own filter slice; carry the old inboxFilter over once.
      migrate: (persisted) => {
        const s = persisted as { inboxFilter?: unknown; taskFilter?: unknown }
        if (!s.taskFilter) s.taskFilter = taskFilterFromLegacy(s.inboxFilter as never)
        delete s.inboxFilter
        return s as ViewState
      },
    },
  ),
)
